import type SettlementCastleBuildingTileEntity from "@/game/entities/buildings/SettlementCastleBuildingTileEntity";
import { tilerepoRoadsFrameNames } from "@/game/entities/config";
import { AchievementStates } from "@/game/entities/config/achievements/achievement_states";
import { AchievementTargetTypes } from "@/game/entities/config/achievements/achievement_target_types";
import { achievements as achievementsConfig } from "@/game/entities/config/achievements/achievements";
import { settlementNames } from "@/game/entities/config/settlements";
import useTileRepository from "@/hooks/useTileRepository";
import type { Achievement, AchievementItem } from "@/types/achievements";
import type { SettlementItem } from "@/types/tile";
import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableState,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { computed, type ComputedRef, type Ref } from "vue";
import useResourcesStore from "./useResourcesStore";
import useUserStore from "./useUserStore";

export type AchievementsStoreState = {
	// Achievements state
	achievements: Array<Achievement>;
	hideClaimed: boolean;
	lastUnlockedTitle: string;
};

type AchievementsStorePayload = {
	state: AchievementsStoreState;
};

type AchievementsStore = {
	$getPayload: () => AchievementsStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: Partial<AchievementsStoreState>) => void;
	achievements: Ref<Array<Achievement>>;
	hideClaimed: ComputedRef<boolean>;
	lastUnlockedTitle: ComputedRef<string>;
	setAchievementState(achievementId: number, state: AchievementStates): void;
	getAchievementById(achievementId: number): Achievement | null;
	setAchievementsState(): void;
	setHideClaimed(value: boolean): void;
	setAchievementProgress(type: AchievementTargetTypes, value: number): void;
	setLastUnlockedTitle(value: string): void;
	getUnclaimedAchievements: ComputedRef<Array<Achievement>>;
	getNoUnclaimedAchievements: ComputedRef<Array<Achievement>>;
	getOpenAchievements: ComputedRef<Array<Achievement>>;
	getClaimedAchievements: ComputedRef<Array<Achievement>>;
};

const STORE_NAME = "achievements";

export default defineStore(STORE_NAME, (): AchievementsStore => {
	const state = createImmutableState<AchievementsStoreState>({
		achievements: Array.from(
			achievementsConfig,
			([id, data]: [number, AchievementItem]): Achievement => {
				return {
					id,
					...data,
					progress: 0,
					state: AchievementStates.OPEN as AchievementStates,
				};
			},
		),
		hideClaimed: false,
		lastUnlockedTitle: "",
	});

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): AchievementsStoreState => {
		const data = getImmutableState(state).value;
		const keys = Object.keys(data) as Array<keyof AchievementsStoreState>;

		return keys.reduce(
			(
				acc: AchievementsStoreState,
				key: keyof AchievementsStoreState,
			): AchievementsStoreState => {
				if (key === "achievements") {
					return {
						...acc,
						[key]: data[key].map(
							(a: Achievement): Achievement => ({
								id: a.id,
								progress: a.progress,
								state: a.state,
							}),
						),
					};
				}
				return {
					...acc,
					[key]: data[key],
				};
			},
			{} as AchievementsStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: AchievementsStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): AchievementsStorePayload | null => {
		if (!$isPatched) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;

		return ($payloadInProgress = {
			state: $getState(),
		});
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Partial<AchievementsStoreState>): void => {
		const configKeys = Array.from(achievementsConfig.keys());
		const missingInIncomingData = configKeys.filter(
			(id: number): boolean => {
				return !value.achievements?.some(
					(a: Achievement): boolean => a.id === id,
				);
			},
		);

		if (missingInIncomingData.length) {
			value.achievements = [
				...(value.achievements ?? []),
				...missingInIncomingData.map(
					(id: number): Achievement => ({
						id,
						...achievementsConfig.get(id),
						progress: 0,
						state: AchievementStates.OPEN,
					}),
				),
			];
		}

		if (value.achievements) {
			value.achievements = value.achievements
				.filter((a: Achievement): boolean => {
					const config = achievementsConfig.get(a.id);
					return !!config;
				})
				.map((a: Achievement): Achievement => {
					const config = achievementsConfig.get(a.id);
					return {
						...a,
						...config,
					};
				});
		}
		setImmutableState(state, value);
		setTimeout((): void => {
			$isPatched = true;
		}, 0);
	};

	const achievements = getImmutableStateProperty(
		state,
		"achievements",
		Array.from(
			achievementsConfig,
			([id, data]: [number, AchievementItem]): Achievement => {
				return {
					id,
					...data,
					progress: 0,
					state: AchievementStates.OPEN,
				};
			},
		),
	);
	const hideClaimed = getImmutableStateProperty(state, "hideClaimed", false);
	const lastUnlockedTitle = getImmutableStateProperty(
		state,
		"lastUnlockedTitle",
		"",
	);

	function setAchievementsState(): void {
		const tileRepo = useTileRepository();
		const resourcesStore = useResourcesStore();
		const userStore = useUserStore();

		setImmutableStateProperty(state, "lastUnlockedTitle", "");

		const castleEntities = Array.from(settlementNames).reduce(
			(
				acc: Array<SettlementCastleBuildingTileEntity>,
				[, { x, y }]: [number, SettlementItem],
			): Array<SettlementCastleBuildingTileEntity> => {
				const index = tileRepo.getTileIndexByTileCoordinates({
					x,
					y,
				});
				const entity = tileRepo.getTileEntityByTileIndex(
					index,
				) as SettlementCastleBuildingTileEntity;
				if (entity?.isRevealed) acc.push(entity);
				return acc;
			},
			new Array<SettlementCastleBuildingTileEntity>(),
		);

		const townsConquered = castleEntities.reduce(
			(
				acc: number,
				entity: SettlementCastleBuildingTileEntity,
			): number => (entity.isActive ? acc + 1 : acc),
			0,
		);
		const mutatedAchievements = achievements.value.map(
			(a: Achievement): Achievement => {
				let progress = a.progress;

				if (!a.target_value) {
					return {
						...a,
						...{ progress: 0, state: AchievementStates.OPEN },
					};
				}
				if (a.state !== AchievementStates.OPEN) return a;

				switch (a.target_type) {
					case AchievementTargetTypes.EXPLORE_TILES:
						progress = tileRepo.exploredTileCount;
						break;
					case AchievementTargetTypes.SETTLEMENT_CONTROL:
						progress = townsConquered;
						break;
					case AchievementTargetTypes.HAS_LUMBER_INCOME:
						progress = resourcesStore.lumberUncappedIncome;
						break;
					case AchievementTargetTypes.HAS_STONE_INCOME:
						progress = resourcesStore.stoneUncappedIncome;
						break;
					case AchievementTargetTypes.HAS_CLAY_INCOME:
						progress = resourcesStore.clayUncappedIncome;
						break;
					case AchievementTargetTypes.HAS_IRON_INCOME:
						progress = resourcesStore.ironUncappedIncome;
						break;
					case AchievementTargetTypes.HAS_GOODS_INCOME:
						progress = resourcesStore.goodsUncappedIncome;
						break;
					case AchievementTargetTypes.HAS_STEEL_INCOME:
						progress = resourcesStore.steelUncappedIncome;
						break;
					case AchievementTargetTypes.HAS_WORKFORCE:
						progress = resourcesStore.populationIncome;
						break;
					case AchievementTargetTypes.HAS_ARMY:
						progress = resourcesStore.armyUncappedIncome;
						break;
					case AchievementTargetTypes.HAS_CLICKERS:
						progress = userStore.superClickersUsed;
						break;
					case AchievementTargetTypes.HAS_ROADS_BUILD:
						progress = tileRepo.getTileCount2(
							tilerepoRoadsFrameNames,
						);
						break;
					case AchievementTargetTypes.AD_WATCH:
						progress = resourcesStore.adsWatchedTotal;
						break;
					default:
						progress = a.progress;
				}
				if (a.target_value) {
					progress = Math.min(progress, a.target_value);
				}
				const state =
					a.target_value && progress >= a.target_value
						? AchievementStates.UNCLAIMED
						: AchievementStates.OPEN;

				return { ...a, ...{ progress: progress, state } };
			},
		);

		setImmutableStateProperty(state, "achievements", mutatedAchievements);
	}

	function setAchievementState(
		achievementId: number,
		newState: AchievementStates,
	): void {
		const newAchievements = achievements.value.map(
			(a: Achievement): Achievement =>
				a.id === achievementId ? { ...a, state: newState } : a,
		);

		setImmutableStateProperty(state, "achievements", newAchievements);
	}

	function setAchievementProgress(
		type: AchievementTargetTypes,
		progress: number,
	): void {
		const achievementsByType = achievements.value.filter(
			(a: Achievement): boolean =>
				a.target_type === type && a.state === AchievementStates.OPEN,
		);

		if (!achievementsByType.length) return;

		const newAchievements = achievements.value.map(
			(a: Achievement): Achievement => {
				if (
					progress > a.progress &&
					a.target_type === type &&
					a.state === AchievementStates.OPEN
				) {
					let newState = a.state as AchievementStates;
					if (a.target_value && progress >= a.target_value) {
						setImmutableStateProperty(
							state,
							"lastUnlockedTitle",
							a.title ?? "",
						);
						newState = AchievementStates.UNCLAIMED;
					}
					return {
						...a,
						progress: Math.min(progress, a.target_value ?? 0),
						state: newState,
					};
				}
				return a;
			},
		);

		setImmutableStateProperty(state, "achievements", newAchievements);
	}

	function setHideClaimed(value: boolean): void {
		setImmutableStateProperty(state, "hideClaimed", value);
	}

	function setLastUnlockedTitle(value: string): void {
		setImmutableStateProperty(state, "lastUnlockedTitle", value);
	}

	function getAchievementById(achievementId: number): Achievement | null {
		if (!achievements.value) return null;
		return (
			Object.values(achievements.value)
				.flat()
				.find((a: Achievement): boolean => a.id === achievementId) ||
			null
		);
	}

	const getUnclaimedAchievements = computed(
		(): Array<Achievement> =>
			achievements.value.filter(
				(a: Achievement): boolean =>
					a.state === AchievementStates.UNCLAIMED,
			),
	);

	const getNoUnclaimedAchievements = computed(
		(): Array<Achievement> =>
			achievements.value.filter(
				(a: Achievement): boolean =>
					a.state !== AchievementStates.UNCLAIMED,
			),
	);

	const getOpenAchievements = computed(
		(): Array<Achievement> =>
			achievements.value.filter(
				(a: Achievement): boolean => a.state === AchievementStates.OPEN,
			),
	);

	const getClaimedAchievements = computed(
		(): Array<Achievement> =>
			achievements.value.filter(
				(a: Achievement): boolean =>
					a.state === AchievementStates.CLAIMED,
			),
	);

	return {
		$getPayload,
		$resetPayload,
		$setState,
		achievements,
		hideClaimed,
		lastUnlockedTitle,
		setAchievementState,
		getAchievementById,
		getUnclaimedAchievements,
		getNoUnclaimedAchievements,
		getOpenAchievements,
		getClaimedAchievements,
		setAchievementsState,
		setHideClaimed,
		setAchievementProgress,
		setLastUnlockedTitle,
	};
});
